import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import toast from 'react-hot-toast';
import { buildApiUrl } from '../utils/api/config';
import './ManageBreakingHeadlines.css';

export default function ManageBreakingHeadlines() {
  const navigate = useNavigate();
  const [headlines, setHeadlines] = useState([]);
  const [newHeadline, setNewHeadline] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [draggedItem, setDraggedItem] = useState(null);

  const LIMIT = 10;

  // Fetch headlines
  const fetchHeadlines = async (pageNum = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        buildApiUrl(`/breaking-headlines/admin/all?page=${pageNum}&limit=${LIMIT}`),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setHeadlines(data.data);
        setTotal(data.total);
        setPage(pageNum);
      }
    } catch (error) {
      toast.error('Failed to fetch headlines: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeadlines(1);
  }, []);

  // Add new headline
  const handleAddHeadline = async (e) => {
    e.preventDefault();
    if (!newHeadline.trim()) {
      toast.error('Please enter a headline');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/breaking-headlines/admin/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ headline: newHeadline })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Headline added successfully');
        setNewHeadline('');
        fetchHeadlines(1);
      } else {
        toast.error(data.message || 'Failed to add headline');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Update headline
  const handleUpdateHeadline = async (id) => {
    if (!editValue.trim()) {
      toast.error('Please enter a headline');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/breaking-headlines/admin/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ headline: editValue })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Headline updated successfully');
        setEditingId(null);
        setEditValue('');
        fetchHeadlines(page);
      } else {
        toast.error(data.message || 'Failed to update headline');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete headline
  const handleDeleteHeadline = async (id) => {
    if (!window.confirm('Are you sure you want to delete this headline?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/breaking-headlines/admin/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Headline deleted successfully');
        fetchHeadlines(page);
      } else {
        toast.error(data.message || 'Failed to delete headline');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle headline status
  const handleToggleStatus = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/breaking-headlines/admin/${id}/toggle`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchHeadlines(page);
      } else {
        toast.error(data.message || 'Failed to toggle status');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Reorder headlines with drag and drop
  const handleDragStart = (item) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropReorder = async (targetItem) => {
    if (!draggedItem || draggedItem._id === targetItem._id) return;

    const draggedIndex = headlines.findIndex(h => h._id === draggedItem._id);
    const targetIndex = headlines.findIndex(h => h._id === targetItem._id);

    const newHeadlines = [...headlines];
    [newHeadlines[draggedIndex], newHeadlines[targetIndex]] = [newHeadlines[targetIndex], newHeadlines[draggedIndex]];
    
    setHeadlines(newHeadlines);
    setDraggedItem(null);

    // Save new order
    try {
      const orderedIds = newHeadlines.map(h => h._id);
      const response = await fetch(buildApiUrl('/breaking-headlines/admin/reorder'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ orderedIds })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Headlines reordered successfully');
      } else {
        toast.error('Failed to reorder headlines');
        fetchHeadlines(page);
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
      fetchHeadlines(page);
    }
  };

  return (
    <AdminShell
      eyebrow="Ticker Control"
      title="Manage breaking headlines"
      description="Update the homepage ticker, reorder the most urgent lines, and keep visibility under direct editorial control."
      primaryAction={{ label: 'Refresh headlines', onClick: () => fetchHeadlines(page) }}
      secondaryAction={{ label: 'Dashboard', onClick: () => navigate('/admin/dashboard') }}
      meta={[
        { label: 'Total headlines', value: String(total) },
        { label: 'Per page', value: String(LIMIT) }
      ]}
    >
      <div className="manage-breaking-headlines-container">

      {/* Add new headline form */}
      <div className="add-headline-section">
        <h2>Add New Headline</h2>
        <form onSubmit={handleAddHeadline} className="add-headline-form">
          <div className="form-group">
            <input
              type="text"
              value={newHeadline}
              onChange={(e) => setNewHeadline(e.target.value)}
              placeholder="Enter news headline in Hindi..."
              maxLength={300}
              disabled={loading}
            />
            <span className="char-count">
              {newHeadline.length}/300
            </span>
          </div>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Adding...' : 'Add Headline'}
          </button>
        </form>
      </div>

      {/* Headlines list */}
      <div className="headlines-list-section">
        <h2>All Headlines ({total})</h2>
        
        {loading && headlines.length === 0 ? (
          <div className="loading-state">Loading headlines...</div>
        ) : headlines.length === 0 ? (
          <div className="empty-state">No headlines found. Add one to get started!</div>
        ) : (
          <>
            <div className="headlines-list">
              {headlines.map((headline, index) => (
                <div
                  key={headline._id}
                  className={`headline-item ${headline.isActive ? 'active' : 'inactive'} ${draggedItem?._id === headline._id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(headline)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropReorder(headline)}
                >
                  <div className="headline-drag-handle">⋮⋮</div>
                  <div className="headline-index">{index + 1}</div>
                  
                  <div className="headline-content">
                    {editingId === headline._id ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                        className="edit-input"
                        maxLength={300}
                      />
                    ) : (
                      <p className="headline-text">{headline.headline}</p>
                    )}
                  </div>

                  <div className="headline-actions">
                    {editingId === headline._id ? (
                      <>
                        <button
                          onClick={() => handleUpdateHeadline(headline._id)}
                          disabled={loading}
                          className="btn-save"
                          title="Save changes"
                        >
                          ✓ Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditValue('');
                          }}
                          className="btn-cancel"
                          title="Cancel editing"
                        >
                          ✕ Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(headline._id);
                            setEditValue(headline.headline);
                          }}
                          disabled={loading}
                          className="btn-edit"
                          title="Edit headline"
                        >
                          ✎ Edit
                        </button>
                        <button
                          onClick={() => handleToggleStatus(headline._id)}
                          disabled={loading}
                          className={`btn-toggle ${headline.isActive ? 'btn-disable' : 'btn-enable'}`}
                          title={headline.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {headline.isActive ? '👁 Hide' : '👁 Show'}
                        </button>
                        <button
                          onClick={() => handleDeleteHeadline(headline._id)}
                          disabled={loading}
                          className="btn-delete"
                          title="Delete headline"
                        >
                          🗑 Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {Math.ceil(total / LIMIT) > 1 && (
              <div className="pagination">
                <button
                  onClick={() => fetchHeadlines(Math.max(1, page - 1))}
                  disabled={page === 1 || loading}
                  className="btn-pagination"
                >
                  ← Previous
                </button>
                <span className="page-info">
                  Page {page} of {Math.ceil(total / LIMIT)}
                </span>
                <button
                  onClick={() => fetchHeadlines(Math.min(Math.ceil(total / LIMIT), page + 1))}
                  disabled={page === Math.ceil(total / LIMIT) || loading}
                  className="btn-pagination"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Info section */}
      <div className="info-section">
        <h3>ℹ️ How it works</h3>
        <ul>
          <li>Adding headlines will display them in the breaking news ticker on the homepage</li>
          <li>Only headlines marked as "Show" will appear in the ticker</li>
          <li>Drag to reorder the headlines - they will display in the order you set</li>
          <li>Maximum 300 characters per headline</li>
          <li>Use Hindi language for maximum reach</li>
        </ul>
      </div>
      </div>
    </AdminShell>
  );
}
